import { defineStore } from "pinia";
import type { ControlModeResponse, Mode, ModeVerdict, WatcherStatus } from "@/types/control";
import { fetchStatus, postControlMode } from "@/api/control";
import { ApiError } from "@/api/request";
import { useAnnouncer } from "@/composables/useAnnouncer";
import { useAccessStore } from "@/stores/access";

export type ControlState = "loading" | "loaded" | "error";

export interface ApplyModeInput {
  to: Mode;
  reason: string;
  confirmation: string;
  /** D-CD-42 — carried through for an upward transition; omitted for a downward one. */
  verdictSig?: ModeVerdict;
}

// useControlStore.applyMode is the single mutation entry point in the whole
// application (§4 store contract) — every other store's actions are GET-only.
export const useControlStore = defineStore("control", {
  state: () => ({
    status: null as WatcherStatus | null,
    state: "loading" as ControlState,
    mutating: false,
    lastResult: null as ControlModeResponse | null,
    lastRefusal: null as string | null,
    apiNotConfigured: false,
    accessRequired: false,
  }),
  actions: {
    async fetchStatus(): Promise<void> {
      const { announce } = useAnnouncer();
      const access = useAccessStore();
      const previous = this.status;
      try {
        const status = await fetchStatus();
        const modeChanged = previous !== null && previous.mode.effective !== status.mode.effective;
        const staleChanged = previous !== null && previous.heartbeat.stale !== status.heartbeat.stale;
        this.status = status;
        this.state = "loaded";
        this.apiNotConfigured = false;
        this.accessRequired = false;
        access.markOk("watcher");
        if (modeChanged) {
          announce(`Effective mode is now ${status.mode.effective}`);
        } else if (staleChanged) {
          announce(status.heartbeat.stale ? "Heartbeat is stale" : "Heartbeat recovered");
        }
      } catch (err) {
        this.state = "error";
        this.apiNotConfigured = err instanceof ApiError && err.notConfigured;
        this.accessRequired = err instanceof ApiError && err.accessRequired;
        if (err instanceof ApiError && err.accessRequired) {
          // D-CD-60 item 6 — stale control state is never shown as current.
          this.status = null;
          access.markRequired("watcher", err.status);
        }
      }
    },

    async applyMode(input: ApplyModeInput): Promise<ControlModeResponse> {
      const { announce } = useAnnouncer();
      const access = useAccessStore();
      this.mutating = true;
      this.lastRefusal = null;
      try {
        const { verdictSig, ...rest } = input;
        const result = await postControlMode({ target: "both", ...rest, verdict_sig: verdictSig });
        this.lastResult = result;
        announce(`Mode changed to ${result.to}`);
        await this.fetchStatus();
        return result;
      } catch (err) {
        const accessRequired = err instanceof ApiError && err.accessRequired;
        const message = accessRequired ? "Sign-in to Watcher required" : err instanceof ApiError ? err.message : "Unknown error";
        this.lastRefusal = message;
        if (accessRequired) access.markRequired("watcher", (err as ApiError).status);
        announce(`Mode change refused: ${message}`);
        throw err;
      } finally {
        this.mutating = false;
      }
    },
  },
});
